import { DownOutlined } from "@ant-design/icons";
import { SubtitlesOutlined } from '@mui/icons-material';
import { Button, Dropdown } from "antd";
import { useAlert } from "components/UI/alertSakeBar/alertProvider";
import { default as React , useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Hls from 'hls.js';
import { useTrackVtt } from "./useTrackVtt";

const VideoPlayerWithTranscript = ({ videoData }) => {
  const { subtitleUrl, subtitleUrlEng } =
    useTrackVtt(videoData);
  const showAlert = useAlert();
  const [selectedLanguage, setSelectedLanguage] = useState("none");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isVideoHovered, setIsVideoHovered] = useState(false);
  const [areControlsVisible, setAreControlsVisible] = useState(true);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const controlsCheckInterval = useRef(null);

  const dataUrl = useMemo(() => videoData?.src, [videoData?.src]);
  const isHls = useMemo(() => typeof dataUrl === 'string' && dataUrl.includes('.m3u8'), [dataUrl]);
  const hlsPoster = useMemo(() => {
    if(videoData?.poster) return videoData?.poster;
    if(isHls){
      const videoId = dataUrl.split('/')[3];
      if(videoId){
        return `https://customer-quvpuvq5vklyms5a.cloudflarestream.com/${videoId}/thumbnails/thumbnail.jpg`;
      }
    }
    return null;
  }, [videoData?.poster, dataUrl, isHls]);

  const subtitleTracks = {
    es: subtitleUrl,
    en: subtitleUrlEng,
  };

  const has_multi =
    typeof subtitleTracks == "object" &&
    subtitleTracks &&
    Object.keys(subtitleTracks).length;

  const selected_track = has_multi
    ? subtitleTracks[selectedLanguage]
    : subtitleUrl;

  const languages = [
    { code: "none", label: "None" },
    { code: "es", label: "ES" },
    { code: "en", label: "EN" },
  ];

  // Check if video controls are visible
  const checkControlsVisibility = () => {
    if (videoRef.current) {
      const video = videoRef.current;

      // Check if controls attribute is present and enabled
      const hasControlsAttribute = video.hasAttribute("controls");

      // Additional check for programmatically hidden controls
      const computedStyle = window.getComputedStyle(video);
      const isControlsHidden =
        computedStyle.getPropertyValue("--webkit-media-controls-panel") ===
          "none" ||
        video.style.display === "none" ||
        video.style.visibility === "hidden";

      const controlsVisible = hasControlsAttribute && !isControlsHidden;

      if (controlsVisible !== areControlsVisible) {
        setAreControlsVisible(controlsVisible);
      }
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHls) return;

    let hls;

    if (Hls.isSupported()) {
      hls = new Hls({ lowLatencyMode: true });
      hls.loadSource(dataUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS.js error:', data);
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = dataUrl;
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, [dataUrl, isHls]);

  useEffect(() => {
    if (videoRef.current) {
      checkControlsVisibility();

      // Set up periodic checking
      controlsCheckInterval.current = setInterval(checkControlsVisibility, 500);

      // Listen for relevant events
      const video = videoRef.current;
      const handleControlsChange = () => checkControlsVisibility();

      video.addEventListener("loadedmetadata", handleControlsChange);
      video.addEventListener("play", handleControlsChange);
      video.addEventListener("pause", handleControlsChange);
      video.addEventListener("seeking", handleControlsChange);
      video.addEventListener("seeked", handleControlsChange);

      // Observer for attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "controls" ||
              mutation.attributeName === "style")
          ) {
            checkControlsVisibility();
          }
        });
      });

      observer.observe(video, {
        attributes: true,
        attributeFilter: ["controls", "style", "class"],
      });

      return () => {
        if (controlsCheckInterval.current) {
          clearInterval(controlsCheckInterval.current);
        }
        video.removeEventListener("loadedmetadata", handleControlsChange);
        video.removeEventListener("play", handleControlsChange);
        video.removeEventListener("pause", handleControlsChange);
        video.removeEventListener("seeking", handleControlsChange);
        video.removeEventListener("seeked", handleControlsChange);
        observer.disconnect();
      };
    }
  }, [areControlsVisible]);

  // Handle transcript language selection
  const handleTranscriptSelection = (languageCode) => {
    setSelectedLanguage(languageCode);
    setIsDropdownVisible(false);

    // Perform your custom action based on selected language
    performTranscriptAction(languageCode);
  };

  // Custom function to handle transcript actions
  const performTranscriptAction = (languageCode) => {

    switch (languageCode) {
      case "none":
        // Hide/disable transcripts
        // Add your logic here - e.g., hide transcript panel, disable captions
        break;
      case "es":
        // Load Spanish transcripts
        // Add your logic here - e.g., fetch Spanish transcript data
        loadTranscript("es");
        break;
      case "en":
        // Load English transcripts
        // Add your logic here - e.g., fetch English transcript data
        loadTranscript("en");
        break;
      default:
    }
  };

  // Function to load transcript based on language
  const loadTranscript = (language) => {
    // Implement your transcript loading logic here
    // This could involve:
    // - Fetching transcript data from API
    // - Updating video player captions
    // - Showing/hiding transcript panel
    // - Synchronizing transcript with video time


    // Example: Enable captions on the player (if supported)
    if (playerRef.current) {
      // You can access the internal player and modify caption settings
      const internalPlayer = playerRef.current.getInternalPlayer();
      // Add caption logic based on your video source
    }
  };

  // Create dropdown menu items
  const menuItems = languages.map((lang) => ({
    key: lang.code,
    label: (
      <MenuItemWrapper
        onClick={() => handleTranscriptSelection(lang.code)}
        isSelected={selectedLanguage === lang.code}
      >
        <span>{lang.label}</span>
        {selectedLanguage === lang.code && <CheckMark>✓</CheckMark>}
      </MenuItemWrapper>
    ),
  }));

  const menu = {
    items: menuItems
  };

  const getCurrentLanguageLabel = () => {
    const currentLang = languages.find(
      (lang) => lang.code === selectedLanguage
    );
    return currentLang ? currentLang.label : "None";
  };

  const showCopyRight = () => {
    showAlert("Content is protected!", "error");
  };

  // Determine if transcript controls should be shown
  const shouldShowTranscriptControls =
    areControlsVisible && (isVideoHovered || isDropdownVisible);

  return (
    <PlayerContainer
      onMouseEnter={() => setIsVideoHovered(true)}
      onMouseLeave={() => {
        setIsVideoHovered(false);
        setIsDropdownVisible(false); // Close dropdown when mouse leaves video area
      }}
      onTouchStart={() => {
        setIsVideoHovered(true);
        setTimeout(() => {
          setIsVideoHovered(false);
        }, 3000);
      }}
    >
      <video
        ref={videoRef}
        className="oneWayVideoContainer1 videoBackground"
        onContextMenu={(e) => {
          e.preventDefault();
          showCopyRight();
        }}
        controls
        autoPlay
        poster={hlsPoster}
        controlsList="nodownload"
        width="100%"
        preload="metadata"
      >
        {!isHls && <source src={videoData?.src} type="video/mp4" />}
        {Boolean(selected_track) && (
          <track
            kind="subtitles"
            src={selected_track}
            srcLang="en"
            label="English"
            default
          />
        )}
      </video>

      <TranscriptControls isVisible={shouldShowTranscriptControls}>
        <Dropdown
          menu={menu}
          overlayClassName="transcript-dropdown-menu"
          trigger={["click"]}
          open={isDropdownVisible}
          onOpenChange={(visible) => {
            setIsDropdownVisible(visible);
            // Keep video hover state active when dropdown is open
            if (visible) {
              setIsVideoHovered(true);
            }
          }}
          placement="topRight"
        >
          <TranscriptButton
            type="default"
            icon={<SubtitlesOutlined />}
            onClick={(e) => e.preventDefault()}
          >
            CC ({getCurrentLanguageLabel()})
            <DownOutlined style={{ marginLeft: 6, fontSize: 10 }} />
          </TranscriptButton>
        </Dropdown>
      </TranscriptControls>
    </PlayerContainer>
  );
};

export default VideoPlayerWithTranscript;

// Styled Components
const PlayerContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const TranscriptControls = styled.div`
  position: absolute;
  // top: 20px;
  right: 20px;
  bottom: 71px;
  z-index: 1000;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  transform: translateY(${(props) => (props.isVisible ? "0" : "-10px")});
  transition: all 0.3s ease;
  pointer-events: ${(props) => (props.isVisible ? "auto" : "none")};
`;

const TranscriptButton = styled(Button)`
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  font-size: 13px;
  padding: 6px 12px;
  height: auto;
  display: flex;
  align-items: center;
  border-radius: 6px;
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.6);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:focus {
    background: rgba(0, 0, 0, 0.9);
    border-color: rgba(255, 255, 255, 0.6);
    color: white;
  }

  &:active {
    transform: translateY(0);
  }

  .anticon {
    color: white;
  }

  transition: all 0.2s ease;
`;

const MenuItemWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  color: white;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CheckMark = styled.div``;
